import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileDown, Upload, Loader2, ShieldAlert, CheckCircle2, AlertTriangle } from "lucide-react";
import { downloadBlob } from "@/components/DataTable";
import MasterHistoricalImport from "@/components/MasterHistoricalImport";

const TEMPLATES = [
  { key: "customer",        title: "Customer Master",              subtitle: "Bulk-load customers with all contact + GST/PAN details." },
  { key: "inquiry",         title: "Inquiry Register",             subtitle: "Historical inquiries with material codes and dimensions." },
  { key: "quotation",       title: "Quotation Register",           subtitle: "Prior quotations with commercial terms and pricing." },
  { key: "po",              title: "Purchase Order Register",      subtitle: "Standalone customer POs — Quotation link optional." },
  { key: "oa",              title: "Order Acceptance Register",    subtitle: "OAs with or without linked Inquiry/Quotation/PO." },
  { key: "cylinder",        title: "Cylinder Register",            subtitle: "Manual 5-digit cylinder numbers with specs." },
  { key: "material_inward", title: "Material Inward Register",     subtitle: "Reconditioning inward — customer DC, RDC and estimated value." },
  { key: "po_oa",           title: "PO + OA (Combined Legacy)",    subtitle: "Legacy combined import with cylinder numbers." },
  { key: "dispatch",        title: "Dispatch Register",            subtitle: "Invoice, transporter, LR + payment against OA/cylinder." },
];

const ERROR_LABEL = {
  blank_identity: "Missing identifier",
  duplicate_cylinder_in_file: "Duplicate cylinder # in file",
  duplicate_inquiry_in_file: "Duplicate inquiry # in file",
  duplicate_quotation_in_file: "Duplicate quotation # in file",
  invalid_date: "Invalid date",
  missing_information: "Missing information",
  customer_not_in_master: "Customer not in master",
  will_update_existing_cylinder: "Will update existing cylinder",
  will_update_existing_inquiry: "Will update existing inquiry",
  will_update_existing_quotation: "Will update existing quotation",
  missing_columns: "Required columns missing",
};

const STATUS_LABEL = {
  ok: "Ready to Import",
  warning: "Ready to Import · Missing Information",
  error: "Cannot Import",
};

const STATUS_ICON = {
  ok: "✓",
  warning: "⚠",
  error: "✕",
};

export default function ImportCentre() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [current, setCurrent] = useState(null); // {key,title}
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Fuzzy header mapping state ---------------------------------------------
  const [mappingReport, setMappingReport] = useState(null);   // from /detect-mapping
  const [override, setOverride] = useState({});               // {header: canonical|null|""}
  const [profiles, setProfiles] = useState([]);
  const [profileLabel, setProfileLabel] = useState("");

  useEffect(() => {
    if (user && !["admin", "super_admin"].includes(user.role)) {
      toast.error("Admin access required");
      nav("/");
    }
  }, [user, nav]);

  async function downloadTemplate(key) {
    const token = localStorage.getItem("hispl_token");
    const res = await fetch(`${API}/import-centre/templates/${key}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Download failed"); return; }
    const spec = TEMPLATES.find((t) => t.key === key);
    await downloadBlob(res, `HISPL_${spec.title.replace(/\s/g, "_")}_Template.xlsx`);
  }

  function openUpload(spec) {
    setCurrent(spec); setFile(null); setPreviewData(null); setImportResult(null);
    setMappingReport(null); setOverride({}); setProfileLabel("");
    loadProfiles(spec.key);
  }

  async function loadProfiles(register) {
    try {
      const { data } = await api.get(`/import-centre/profiles/${register}`);
      setProfiles(data.profiles || []);
    } catch { setProfiles([]); }
  }

  async function detectMapping(chosenFile) {
    if (!chosenFile || !current) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", chosenFile);
      const { data } = await api.post(`/import-centre/detect-mapping/${current.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMappingReport(data);
      setOverride({});           // start from auto-detected
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Column detection failed"); }
    finally { setLoading(false); }
  }

  function currentMapping() {
    // Merge auto-detected mapping with user overrides — overrides win.
    if (!mappingReport) return {};
    const merged = { ...(mappingReport.mapping || {}) };
    for (const [k, v] of Object.entries(override)) {
      merged[k] = v === "" ? null : v;
    }
    return merged;
  }

  async function runPreview() {
    if (!file || !current) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (mappingReport) fd.append("mapping", JSON.stringify(currentMapping()));
      const { data } = await api.post(`/import-centre/preview/${current.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed"); }
    finally { setLoading(false); }
  }

  async function runImport() {
    if (!file || !current) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (mappingReport) fd.append("mapping", JSON.stringify(currentMapping()));
      const { data } = await api.post(`/import-centre/import/${current.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      if (data.imported > 0) toast.success(`Imported ${data.imported} records`);
      else toast.warning("No records imported — see summary below");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed"); }
    finally { setLoading(false); }
  }

  async function saveProfile() {
    if (!current || !mappingReport || !profileLabel.trim()) {
      toast.error("Give the profile a name first"); return;
    }
    try {
      await api.post(`/import-centre/profiles/${current.key}`, {
        label: profileLabel.trim(), mapping: currentMapping(),
      });
      toast.success("Mapping profile saved");
      setProfileLabel("");
      loadProfiles(current.key);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
  }

  function applyProfile(p) {
    // Overlay the profile mapping onto the current headers
    if (!mappingReport) return;
    const next = {};
    for (const h of mappingReport.incoming_headers || []) {
      if (p.mapping[h] !== undefined) next[h] = p.mapping[h] === null ? "" : p.mapping[h];
    }
    setOverride(next);
    toast.success(`Applied profile: ${p.label}`);
  }

  async function deleteProfile(p) {
    if (!current) return;
    try {
      await api.delete(`/import-centre/profiles/${current.key}/${p.id}`);
      toast.success("Profile deleted");
      loadProfiles(current.key);
    } catch { toast.error("Could not delete profile"); }
  }

  return (
    <div data-testid="import-centre-page" className="space-y-6" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="rounded-lg p-5 border border-slate-200" style={{ backgroundColor: "#F7F8FA" }}>
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-5 h-5" style={{ color: "#C62828" }} />
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#C62828" }}>
              Administration · Restricted
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mt-0.5" style={{ color: "#0B1F3A" }}>Import Centre</h1>
            <p className="text-sm text-slate-600 mt-1">
              Historical data migration + future bulk uploads. Download the template, fill it, upload and preview before committing. <strong>Admin-only.</strong>
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TEMPLATES.map((t) => (
          <Card key={t.key} data-testid={`import-tile-${t.key}`} className="border-slate-200 p-5 rounded-lg">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold" style={{ color: "#0B1F3A" }}>{t.title}</div>
                <div className="text-xs text-slate-500 mt-1">{t.subtitle}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" data-testid={`import-download-${t.key}`}
                        onClick={() => downloadTemplate(t.key)}>
                  <FileDown className="w-3.5 h-3.5 mr-1" /> Template
                </Button>
                <Button size="sm" data-testid={`import-upload-${t.key}`}
                        onClick={() => openUpload(t)}
                        className="text-white" style={{ backgroundColor: "#C62828" }}>
                  <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Master Historical Data Import — single-file bulk migration */}
      <MasterHistoricalImport />

      <Dialog open={!!current} onOpenChange={(v) => { if (!v) { setCurrent(null); setPreviewData(null); setImportResult(null); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Import — {current?.title}</DialogTitle>
            <DialogDescription>Preview validates the file. Rows with errors are skipped on import. Duplicates are auto-skipped.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <input
              type="file" accept=".xlsx,.xls,.csv"
              data-testid="import-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFile(f); setPreviewData(null); setImportResult(null);
                setMappingReport(null); setOverride({});
                if (f) detectMapping(f);
              }}
              className="text-xs w-full border border-slate-300 rounded-lg px-2 py-2"
            />

            {/* Column-mapping review — shown after file is picked and detect-mapping returns */}
            {mappingReport && (
              <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="column-mapping-review">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm font-semibold" style={{ color: "#0B1F3A" }}>
                    Column Mapping
                    <span className="ml-2 font-mono text-xs text-slate-500">
                      {mappingReport.incoming_headers?.length || 0} in file · {mappingReport.canonical_fields?.length || 0} canonical
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {profiles.length > 0 && (
                      <select
                        className="text-xs border border-slate-300 rounded px-2 py-1"
                        data-testid="import-profile-select"
                        onChange={(e) => {
                          const p = profiles.find((x) => x.id === e.target.value);
                          if (p) applyProfile(p);
                        }}
                        defaultValue=""
                      >
                        <option value="" disabled>Load saved mapping…</option>
                        {profiles.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                      </select>
                    )}
                  </div>
                </div>
                <div className="max-h-60 overflow-auto border border-slate-100 rounded">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1 text-left font-semibold text-slate-700 border-b">Your File Column</th>
                        <th className="px-2 py-1 text-left font-semibold text-slate-700 border-b">Maps To (ERP Field)</th>
                        <th className="px-2 py-1 text-left font-semibold text-slate-700 border-b w-16">Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(mappingReport.incoming_headers || []).map((h) => {
                        const chosen = override[h] !== undefined ? override[h] : mappingReport.mapping[h];
                        const conf = override[h] !== undefined ? 100 : (mappingReport.confidence[h] || 0);
                        return (
                          <tr key={h} className="border-b hover:bg-slate-50" data-testid={`map-row-${h}`}>
                            <td className="px-2 py-1 font-mono truncate max-w-[200px]">{h}</td>
                            <td className="px-2 py-1">
                              <select
                                className="text-xs border border-slate-300 rounded px-1 py-0.5 w-full max-w-[280px]"
                                value={chosen || ""}
                                onChange={(e) => setOverride({ ...override, [h]: e.target.value })}
                                data-testid={`map-select-${h}`}
                              >
                                <option value="">— Ignore this column —</option>
                                {(mappingReport.canonical_fields || []).map((c) => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-2 py-1 font-mono">
                              {chosen ? (
                                <span className={conf >= 90 ? "text-emerald-700" : conf >= 75 ? "text-amber-700" : "text-slate-500"}>
                                  {conf}%
                                </span>
                              ) : <span className="text-slate-400">skip</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {mappingReport.missing_canonical?.length > 0 && (
                  <div className="text-[11px] text-slate-500">
                    Not in file (optional): <span className="font-mono">{mappingReport.missing_canonical.join(", ")}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="text" placeholder="Name this mapping (e.g. L&T Format)"
                    value={profileLabel} onChange={(e) => setProfileLabel(e.target.value)}
                    className="text-xs border border-slate-300 rounded px-2 py-1 flex-1"
                    data-testid="import-profile-label"
                  />
                  <Button size="sm" variant="outline" onClick={saveProfile} data-testid="import-profile-save">
                    Save Profile
                  </Button>
                </div>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={runPreview} disabled={!file || loading}
                      data-testid="import-preview-btn">
                {loading && !importResult ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview"}
              </Button>
              <Button onClick={runImport} disabled={!file || loading}
                      data-testid="import-commit-btn"
                      className="text-white" style={{ backgroundColor: "#C62828" }}>
                {loading && importResult ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}
              </Button>
            </div>

            {previewData && (
              <div className="space-y-3" data-testid="import-preview-result">
                <div className="flex items-center gap-3 text-xs flex-wrap">
                  <span className="font-semibold">Rows found:</span>
                  <span className="font-mono">{previewData.row_count}</span>
                  {previewData.status_counts?.ok > 0 && (
                    <span data-testid="status-count-ok" className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 font-mono text-[10px]">
                      ✓ {previewData.status_counts.ok} ready
                    </span>
                  )}
                  {previewData.status_counts?.warning > 0 && (
                    <span data-testid="status-count-warning" className="inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 font-mono text-[10px]">
                      ⚠ {previewData.status_counts.warning} ready · missing info
                    </span>
                  )}
                  {previewData.status_counts?.error > 0 && (
                    <span data-testid="status-count-error" className="inline-flex items-center gap-1 text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 font-mono text-[10px]">
                      ✕ {previewData.status_counts.error} cannot import
                    </span>
                  )}
                </div>

                {previewData.errors?.some((e) => e.type === "missing_columns") && (
                  <div className="border border-red-300 bg-red-50 rounded-lg p-2 text-xs text-red-800 font-mono">
                    File is missing required columns: {previewData.errors.filter((e) => e.type === "missing_columns").map((e) => (Array.isArray(e.detail) ? e.detail.join(", ") : e.detail)).join(", ")}
                  </div>
                )}

                {previewData.row_statuses?.length > 0 && (
                  <div className="max-h-52 overflow-auto border border-slate-200 rounded-lg" data-testid="row-status-list">
                    <table className="w-full text-[11px]">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left font-semibold text-slate-700 border-b w-14">Row</th>
                          <th className="px-2 py-1 text-left font-semibold text-slate-700 border-b w-28">Status</th>
                          <th className="px-2 py-1 text-left font-semibold text-slate-700 border-b">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.row_statuses.slice(0, 100).map((s, i) => {
                          const cls = s.status === "error"
                            ? "bg-red-50/60 text-red-800"
                            : s.status === "warning"
                            ? "bg-amber-50/50 text-amber-900"
                            : "bg-emerald-50/40 text-emerald-800";
                          return (
                            <tr key={i} className={`border-b ${cls}`} data-testid={`row-status-${s.row}`}>
                              <td className="px-2 py-1 font-mono">{s.row}</td>
                              <td className="px-2 py-1 font-mono font-semibold whitespace-nowrap">
                                {STATUS_ICON[s.status]} {STATUS_LABEL[s.status]}
                              </td>
                              <td className="px-2 py-1 font-mono">
                                {[...(s.errors || []), ...(s.warnings || [])]
                                  .map((n) => `${ERROR_LABEL[n.type] || n.type}${n.field ? ` — ${n.field}` : ""}${n.value ? ` (${n.value})` : ""}`)
                                  .join(" · ") || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {previewData.preview?.length > 0 && (
                  <div className="border border-slate-200 rounded-lg overflow-auto max-h-56">
                    <table className="w-full text-[11px]">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          {Object.keys(previewData.preview[0]).slice(0, 8).map((k) => (
                            <th key={k} className="px-2 py-1.5 text-left font-semibold text-slate-700 border-b">{k}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.preview.slice(0, 10).map((row, i) => (
                          <tr key={i} className="border-b hover:bg-slate-50">
                            {Object.keys(previewData.preview[0]).slice(0, 8).map((k) => (
                              <td key={k} className="px-2 py-1 font-mono text-slate-700 truncate max-w-[140px]">
                                {String(row[k] ?? "")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {importResult && (
              <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="import-result">
                <div className="text-sm font-semibold" style={{ color: "#0B1F3A" }}>Import Summary</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Imported</div>
                    <div className="font-mono text-2xl text-emerald-700 font-bold" data-testid="stat-imported">{importResult.imported}</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                    <div className="text-[10px] uppercase tracking-wider text-blue-800 font-semibold">Updated</div>
                    <div className="font-mono text-2xl text-blue-700 font-bold" data-testid="stat-updated">{importResult.updated || 0}</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                    <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold">Skipped</div>
                    <div className="font-mono text-2xl text-amber-700 font-bold" data-testid="stat-skipped">{importResult.skipped}</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                    <div className="text-[10px] uppercase tracking-wider text-red-800 font-semibold">Failed</div>
                    <div className="font-mono text-2xl text-red-700 font-bold" data-testid="stat-failed">{importResult.failed?.length || 0}</div>
                  </div>
                </div>

                {importResult.conflicts?.length > 0 && (
                  <div className="mt-2 border border-amber-300 bg-amber-50/40 rounded-lg p-2 text-xs" data-testid="conflicts-panel">
                    <div className="font-semibold text-amber-900 mb-1">
                      {importResult.conflicts.length} conflict(s) — existing non-empty values were <em>not</em> overwritten. Use Commercial Link Manager or the record's edit page to reconcile.
                    </div>
                    <div className="max-h-40 overflow-auto">
                      <table className="w-full text-[11px]">
                        <thead className="text-amber-900">
                          <tr><th className="px-2 py-1 text-left">Row</th><th className="px-2 py-1 text-left">Entity</th><th className="px-2 py-1 text-left">ID</th><th className="px-2 py-1 text-left">Field</th><th className="px-2 py-1 text-left">Existing</th><th className="px-2 py-1 text-left">Incoming</th></tr>
                        </thead>
                        <tbody className="font-mono">
                          {importResult.conflicts.slice(0, 30).flatMap((c, i) =>
                            (c.conflicts || []).map((cf, j) => (
                              <tr key={`${i}-${j}`} className="border-t border-amber-100 text-amber-800" data-testid={`conflict-${c.row}-${cf.field}`}>
                                <td className="px-2 py-1">{c.row}</td>
                                <td className="px-2 py-1">{c.entity}</td>
                                <td className="px-2 py-1">{c.identifier}</td>
                                <td className="px-2 py-1">{cf.field}</td>
                                <td className="px-2 py-1 text-slate-700">{String(cf.existing ?? "")}</td>
                                <td className="px-2 py-1 font-semibold">{String(cf.incoming ?? "")}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(importResult.log?.length > 0 || importResult.failed?.length > 0) && (
                  <div className="max-h-32 overflow-auto text-[10px] font-mono space-y-0.5 border border-slate-200 rounded p-2 bg-slate-50">
                    {(importResult.log || []).map((l, i) => <div key={`l${i}`} className="text-amber-700">Row {l.row}: {l.reason}</div>)}
                    {(importResult.failed || []).map((f, i) => <div key={`f${i}`} className="text-red-700">Row {f.row}: {f.error}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setCurrent(null); setPreviewData(null); setImportResult(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
